import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Grid,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon
} from '@mui/icons-material';
import { authGet, authPut } from '../../services/apiService';
import { format } from 'date-fns';

/**
 * TSIAdminConfiguration component for managing TSI ticket configuration
 * Allows admins to configure email addresses for:
 * - SRE Group
 * - Professional Service Group
 * - Client Support Team
 * @returns {JSX.Element} TSIAdminConfiguration component
 */
const TSIAdminConfiguration = () => {
  const [sreGroup, setSreGroup] = useState('');
  const [professionalServiceGroup, setProfessionalServiceGroup] = useState('');
  const [clientSupportTeam, setClientSupportTeam] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  // Dark green color matching the theme
  const darkGreenColor = "#1D4434";

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return format(new Date(dateString), 'MMM d, yyyy HH:mm');
    } catch (err) {
      return 'Invalid date';
    }
  };

  // Fetch current configuration
  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authGet('/api/admin/ado-email-groups');
      
      if (response.success && response.data) {
        setSreGroup(response.data.sreGroup || '');
        setProfessionalServiceGroup(response.data.professionalServiceGroup || '');
        setClientSupportTeam(response.data.clientSupportTeam || '');
        setUpdatedAt(response.data.updatedAt);
      } else {
        setError('Failed to load ADO email groups configuration');
      }
    } catch (err) {
      console.error('Error fetching ADO email groups config:', err);
      setError(err.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  // Save configuration
  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccess(null);
      setError(null);

      const response = await authPut('/api/admin/ado-email-groups', {
        sreGroup,
        professionalServiceGroup,
        clientSupportTeam
      });

      if (response.success) {
        setSuccess('ADO email groups saved successfully');
        setUpdatedAt(response.data.updatedAt);
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(response.message || 'Failed to save configuration');
      }
    } catch (err) {
      console.error('Error saving ADO email groups config:', err);
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Load configuration on mount
  useEffect(() => {
    fetchConfig();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, color: darkGreenColor, fontWeight: 'bold' }}>
        TSI Tickets Configuration
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure email addresses (comma-separated) for groups of people who have created ADO work items.
      </Typography>

      {/* Success Message */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* SRE Group */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="SRE Group"
              value={sreGroup}
              onChange={(e) => setSreGroup(e.target.value)}
              placeholder="email1@example.com, email2@example.com, email3@example.com"
              helperText="Enter comma-separated email addresses for SRE Group members"
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Professional Service Group */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Professional Service Group"
              value={professionalServiceGroup}
              onChange={(e) => setProfessionalServiceGroup(e.target.value)}
              placeholder="email1@example.com, email2@example.com, email3@example.com"
              helperText="Enter comma-separated email addresses for Professional Service Group members"
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Client Support Team */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Client Support Team"
              value={clientSupportTeam}
              onChange={(e) => setClientSupportTeam(e.target.value)}
              placeholder="email1@example.com, email2@example.com, email3@example.com"
              helperText="Enter comma-separated email addresses for Client Support Team members"
              disabled={saving}
            />
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              {updatedAt && (
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  Last updated: {formatDate(updatedAt)}
                </Typography>
              )}
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                sx={{
                  backgroundColor: darkGreenColor,
                  '&:hover': { backgroundColor: '#0f2e1f' }
                }}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default TSIAdminConfiguration;

